import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import "./addsalelead.scss";

import {
  addSalesLead,
  buttonName,
  editSalesLead,
  alertActions
} from "../../locales/en";

import {
  COMMISSION_RATE_REGEX,
  showToastType
} from "../../utils/constants/appConstants";

import { editSalesLeadThunk } from "../../redux/salesLeadSlice";
import privateAxios from "../../utils/axiosPrivate";
import { getSalesEditURL } from "../../utils/constants/apiEndPoints";
import { sanitizeInput, showToast } from "../../utils/utils";
import { SALES_LEADS } from "../../route/paths";

const EditSaleLead = () => {
  const { rowId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const initialRender = useRef(true);

  const normalize = (v) => (v === null || v === undefined ? "" : v);

  const initialValue = {
    name: "",
    nickName: "",
    email: "",
    phone: "",
    isDirectClientCommission: false,
    highTouchCommissionRate: "",
    lowTouchCommissionRate: "",
    isReferralCommission: false,
    referralCommissionRate: ""
  };

  const [saleUser, setSaleUser] = useState(initialValue);
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    name,
    nickName,
    email,
    phone,
    isDirectClientCommission,
    highTouchCommissionRate,
    lowTouchCommissionRate,
    isReferralCommission,
    referralCommissionRate
  } = saleUser;

  const loadUser = async () => {
    try {
      const response = await dispatch(
        editSalesLeadThunk(Number(rowId))
      ).unwrap();

      const data = response?.data?.data;

      if (data) {
        setSaleUser({
          name: normalize(data.name),
          nickName: normalize(data.nickName),
          email: normalize(data.email),
          phone: normalize(data.phone),
          isDirectClientCommission: !!data.isDirectClientCommission,
          highTouchCommissionRate: normalize(data.highTouchCommissionRate),
          lowTouchCommissionRate: normalize(data.lowTouchCommissionRate),
          isReferralCommission: !!data.isReferralCommission,
          referralCommissionRate: normalize(data.referralCommissionRate)
        });
      }
    } catch {
      // handle error
    }
  };

  useEffect(() => {
    if (initialRender.current && rowId) {
      loadUser();
      initialRender.current = false;
    }
  }, [rowId]);

  const onInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    const newValue =
      type === "checkbox" ? checked : sanitizeInput(value).trim();

    setSaleUser((prev) => ({ ...prev, [name]: newValue }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const tempErrors = {};

    if (
      highTouchCommissionRate &&
      (!COMMISSION_RATE_REGEX.test(highTouchCommissionRate) ||
        parseFloat(highTouchCommissionRate) >= 100)
    ) {
      tempErrors.highTouchCommissionRate = addSalesLead.errMsg.item6;
    }

    if (
      lowTouchCommissionRate &&
      (!COMMISSION_RATE_REGEX.test(lowTouchCommissionRate) ||
        parseFloat(lowTouchCommissionRate) >= 100)
    ) {
      tempErrors.lowTouchCommissionRate = addSalesLead.errMsg.item7;
    }

    if (
      referralCommissionRate &&
      (!COMMISSION_RATE_REGEX.test(referralCommissionRate) ||
        parseFloat(referralCommissionRate) >= 100)
    ) {
      tempErrors.referralCommissionRate = addSalesLead.errMsg.item8;
    }

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setMessage("");

    if (!validate()) return;

    try {
      setIsSubmitting(true);
      const response = await privateAxios.put(getSalesEditURL(rowId), saleUser);

      showToast(response.data.message, showToastType.success);
      setTimeout(() => navigate(SALES_LEADS), 1500);
    } catch (error) {
      showToast(
        error.message || addSalesLead.errMsg.item11,
        showToastType.error
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(SALES_LEADS);
  };

  return (
    <>
      <div className="row mb-2">
        <div className="col-12 sales-page-title-wrap pb-2">
          <div className="box-heading d-flex flex-wrap align-items-center">
            <h2>{addSalesLead?.mainTitle}</h2>
            <p className="mb-0">{editSalesLead?.subH2}</p>
          </div>
        </div>
      </div>
      <div className="row sales-page-details-wrap mb-4">
        <div className="col-12 d-flex flex-wrap align-items-center">
          <div className="box-title py-2">
            <h3>{editSalesLead?.subH2}</h3>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-md-8 sale-leade-form offset-md-2">
          {message && (
            <div className="alert alert-success mt-2 mb-2">{message}</div>
          )}
          <form onSubmit={handleSubmit}>
            {/* name */}
            <div className="row mb-2">
              <div className="col-sm-4">
                <div className="input-title">
                  <label htmlFor="name">{addSalesLead?.fullName}</label>
                </div>
              </div>
              <div className="col-sm-8">
                <div className="input-group">
                  <input
                    type="text"
                    placeholder="Full Name"
                    name="name"
                    id="name"
                    readOnly
                    value={name}
                  />
                  {errors.name && (
                    <div id="nameError" className="text-danger small">
                      {errors.name}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="row mb-2">
              <div className="col-sm-4">
                <div className="input-title">
                  <label htmlFor="email">{addSalesLead?.mailAddrs}</label>
                </div>
              </div>
              <div className="col-sm-8">
                <div className="input-group">
                  <input
                    type="email"
                    placeholder="Email Address"
                    name="email"
                    id="email"
                    onChange={onInputChange}
                    value={email}
                  />
                  {errors.email && (
                    <div id="emailError" className="text-danger small">
                      {errors.email}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="row mb-2">
              <div className="col-sm-4">
                <div className="input-title">
                  <label htmlFor="nickName">{addSalesLead?.nickName}</label>
                </div>
              </div>
              <div className="col-sm-8">
                <div className="input-group">
                  <input
                    type="text"
                    placeholder="Nick Name"
                    name="nickName"
                    id="nickName"
                    value={nickName}
                    onChange={onInputChange}
                  />
                  {errors.nickName && (
                    <div className="text-danger small">{errors.nickName}</div>
                  )}
                </div>
              </div>
            </div>

            {/* phone number */}
            <div className="row mb-3">
              <div className="col-sm-4">
                <div className="input-title">
                  <label htmlFor="phone">{addSalesLead?.number}</label>
                </div>
              </div>
              <div className="col-sm-8">
                <div className="input-group">
                  <input
                    type="tel"
                    placeholder="Contact Number"
                    name="phone"
                    id="phonenumber"
                    value={phone}
                    onChange={onInputChange}
                  />
                  {errors.phone && (
                    <div className="text-danger small">{errors.phone}</div>
                  )}
                </div>
              </div>
            </div>

            {/* direct client commission */}
            <div className="row mb-3">
              <div className="col-sm-4">
                <div className="input-title">
                  <label htmlFor="isDirectClientCommission">
                    {addSalesLead?.clientCommison}
                  </label>
                </div>
              </div>
              <div className="col-sm-8">
                <div className="input-group">
                  <input
                    type="checkbox"
                    name="isDirectClientCommission"
                    id="isDirectClientCommission"
                    checked={isDirectClientCommission}
                    onChange={onInputChange}
                  />
                </div>
              </div>
            </div>

            <div className="row mb-2">
              <div className="col-sm-4">
                <div className="input-title">
                  <label htmlFor="highTouchCommissionRate">
                    {addSalesLead?.highTouchComsn}
                  </label>
                </div>
              </div>
              <div className="col-sm-8">
                <div className="input-group">
                  <input
                    type="text"
                    placeholder="High Touch Commission Rate%"
                    name="highTouchCommissionRate"
                    id="highTouchCommissionRate"
                    value={highTouchCommissionRate}
                    onChange={onInputChange}
                  />
                  {errors.highTouchCommissionRate && (
                    <div className="text-danger small">
                      {errors.highTouchCommissionRate}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-sm-4">
                <div className="input-title">
                  <label htmlFor="lowTouchCommissionRate">
                    {addSalesLead?.lowTouchComsn}
                  </label>
                </div>
              </div>
              <div className="col-sm-8">
                <div className="input-group">
                  <input
                    type="text"
                    placeholder="Low Touch Commission Rate%"
                    name="lowTouchCommissionRate"
                    id="lowTouchCommissionRate"
                    value={lowTouchCommissionRate}
                    onChange={onInputChange}
                  />
                  {errors.lowTouchCommissionRate && (
                    <div className="text-danger small">
                      {errors.lowTouchCommissionRate}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-sm-4">
                <div className="input-title">
                  <label htmlFor="isReferralCommission">
                    {addSalesLead?.referralCommsn}
                  </label>
                </div>
              </div>
              <div className="col-sm-8">
                <div className="input-group">
                  <input
                    type="checkbox"
                    name="isReferralCommission"
                    id="isReferralCommission"
                    checked={isReferralCommission}
                    onChange={onInputChange}
                  />
                </div>
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-sm-4">
                <div className="input-title">
                  <label htmlFor="referralCommissionRate">
                    {addSalesLead?.referralCommsnRate}
                  </label>
                </div>
              </div>
              <div className="col-sm-8">
                <div className="input-group">
                  <input
                    type="text"
                    placeholder="Referral Commission Rate%"
                    name="referralCommissionRate"
                    id="referralCommissionRate"
                    value={referralCommissionRate}
                    onChange={onInputChange}
                  />
                  {errors.referralCommissionRate && (
                    <div
                      id="referralCommissionRateError"
                      className="text-danger small"
                    >
                      {errors.referralCommissionRate}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-sm-8 offset-sm-4">
                <button
                  type="submit"
                  className="btn btn-primary me-2"
                  disabled={isSubmitting}
                >
                  {buttonName?.update}
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleCancel}
                >
                  {alertActions?.Cancel}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default EditSaleLead;
